import { useMutation } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { createIdempotencyKey } from "@/core/api/idempotency";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type {
  StatementRequestDto,
  StatementRequestResponse,
} from "@/features/wallet/api/dto";

interface Args {
  body: StatementRequestDto;
  /**
   * Clé d'idempotence (§4.7, obligatoire d'après la collection v2.5 sur
   * `Statements/request`). Si omise, une clé est générée automatiquement —
   * **à éviter côté formulaire** (passer `createIdempotencyScope().key` et
   * `renew()` à chaque nouvelle tentative métier).
   */
  idempotencyKey?: string;
}

/**
 * Demande un relevé d'opérations (`POST /Statements/request`, guide v2.3 §6.3).
 *
 * wallet-service compose un PDF et le fait expédier par notification-service
 * en pièce jointe à l'adresse enregistrée du titulaire. `walletId` est
 * obligatoire pour un compte back-office ou multi-portefeuilles.
 * Ouvert à tout titulaire ; aucun rôle particulier. **Idempotency-Key
 * obligatoire** (collection v2.5).
 */
export const useRequestStatement = () =>
  useMutation({
    mutationKey: ["wallet", "statements", "request"],
    mutationFn: ({ body, idempotencyKey }: Args) =>
      mutateData({
        url: walletEndpoints.statements.request,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
        idempotencyKey: idempotencyKey ?? createIdempotencyKey(),
      }) as Promise<StatementRequestResponse>,
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useRequestStatement failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
